import React from "react";
import { Spinner } from "@cinesys/ui/components";
import InterfaceAccessNotice from "../../components/InterfaceAccessNotice";
import { ApiError } from "../../CustomerApiService";

/**
 * Gates a baseline, flag-only surface (home-dashboard, item-detail — Phase 2) that ISN'T a
 * provisioned plugin instance, so `useInterfaceAccess`/`enabledInterfaces` doesn't apply to it.
 * Its own route already carries the `enabled` verdict in the SAME response as the real payload
 * (`resolveSetting` server-side, see `apps/customer-api/src/routes/home-dashboard`/`item-detail`).
 *
 * Unlike `useInterfaceAccess` (whose query IS the auth/entitlement check, so any error there
 * really is "can't verify you"), the queries this gates are content fetches — a bad/deleted asset
 * id is a real, unrelated failure. `deriveFeatureGateState` below tells a genuine auth failure
 * (401/403) apart from a content 404 or any other error, using `ApiError.status`, which already
 * carries the real HTTP status end-to-end from the backend.
 */
export interface FeatureGateQuery {
  isLoading: boolean;
  error: unknown;
  enabled: boolean | undefined;
}

export type FeatureGateState =
  | { kind: "loading" }
  | {
      kind: "notice";
      reason: "unauthenticated" | "not-enabled" | "not-found" | "error";
    }
  | { kind: "enabled" };

/**
 * Pure branching behind `FeatureGate`, split out so it's unit-testable without a `QueryClient`/
 * rendered component — same pattern as `useInterfaceAccess.ts`'s `deriveInterfaceAccess`.
 */
export function deriveFeatureGateState(
  query: FeatureGateQuery
): FeatureGateState {
  if (query.error) {
    if (query.error instanceof ApiError) {
      if (query.error.status === 401 || query.error.status === 403) {
        return { kind: "notice", reason: "unauthenticated" };
      }
      if (query.error.status === 404) {
        return { kind: "notice", reason: "not-found" };
      }
    }
    return { kind: "notice", reason: "error" };
  }
  if (query.isLoading || query.enabled === undefined) {
    return { kind: "loading" };
  }
  if (!query.enabled) {
    return { kind: "notice", reason: "not-enabled" };
  }
  return { kind: "enabled" };
}

export interface FeatureGateProps {
  isLoading: boolean;
  error: unknown;
  enabled: boolean | undefined;
  children: React.ReactNode;
}

const FeatureGate = ({
  isLoading,
  error,
  enabled,
  children,
}: FeatureGateProps) => {
  const state = deriveFeatureGateState({ isLoading, error, enabled });
  if (state.kind === "notice") {
    return <InterfaceAccessNotice reason={state.reason} />;
  }
  if (state.kind === "loading") {
    return <Spinner />;
  }
  return <>{children}</>;
};

export default FeatureGate;
